import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Clock, CreditCard, Lock, UserX, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const VARIANTS = {
  demo_expired: {
    icon: Clock, title: "Vaše demo skončilo",
    text: "Zkušební období aplikace Proyekty vypršelo. Pokud chcete pokračovat a zvolit si tarif, ozvěte se nám — přístup obnovíme obratem.",
    contacts: true,
  },
  past_due: {
    icon: CreditCard, title: "Předplatné čeká na úhradu",
    text: "Zatím se nám nepodařilo spárovat platbu za toto období. Jakmile platba dorazí, přístup se obnoví obratem.",
    contacts: true, note: "Vaše data i rozdělaná práce zůstávají beze změny uložena.",
  },
  // 206: předplatné fakturou — nezaplaceno (bez Stripe portálu, platí se převodem)
  invoice_unpaid: {
    icon: CreditCard, title: "Předplatné čeká na úhradu",
    text: "Platba za předplatné k nám zatím nedorazila, proto je přístup pozastavený. Jakmile platbu připíšeme, odemkneme ho obratem.",
    contacts: true, note: "Vaše data i rozdělaná práce zůstávají beze změny uložena.",
  },
  ended: {
    icon: Lock, title: "Přístup byl ukončen",
    text: "Spolupráce byla ukončena a přístup do aplikace uzavřen. Vaše data byla připravena k předání. Pokud se chcete vrátit nebo cokoli doladit, ozvěte se nám.",
    contacts: true,
  },
  suspended: {
    icon: Lock, title: "Přístup je dočasně pozastaven",
    text: "Váš přístup do aplikace Proyekty je momentálně pozastaven. Vaše data zůstávají beze změny uložena. Ozvěte se nám a domluvíme se, jak dál.",
    contacts: true,
  },
  inactive: {
    icon: UserX, title: "Účet není aktivní",
    text: "Přístup vaší firmy do aplikace Proyekty momentálně není aktivní. Obraťte se prosím na majitele nebo administrátora vaší společnosti.",
    contacts: false,
  },
};

export default function Locked({ reason }) {
  const { logout, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const v = VARIANTS[reason] || VARIANTS.inactive;
  const Icon = v.icon;
  // Při neuhrazené platbě si majitel opraví kartu ve Stripe portálu —
  // po stržení platby webhookem se firma odemkne sama.
  const showPortal = user?.role === "OWNER" && reason === "past_due";
  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="locked-screen">
      <div className="w-full max-w-[560px] bg-card border border-border rounded-[20px] shadow-xl px-8 py-10 sm:px-12 flex flex-col items-center text-center">
        <div className="text-2xl font-bold tracking-tight mb-8">Proyekty</div>
        <div className="w-[72px] h-[72px] rounded-full bg-brand/10 grid place-items-center mb-5">
          <Icon size={34} className="text-brand" />
        </div>
        <h1 className="font-heading text-[27px] font-extrabold tracking-tight mb-3" data-testid="locked-title">{v.title}</h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground mb-7" data-testid="locked-text">{v.text}</p>
        {showPortal && (
          <button type="button" onClick={openPortal} disabled={busy} data-testid="locked-portal-btn"
            className="w-full h-[50px] rounded-[14px] bg-brand text-white font-bold flex items-center justify-center gap-2 mb-3">
            <CreditCard size={18} /> {busy ? "Otevírám…" : "Aktualizovat platební kartu"}
          </button>
        )}
        {v.contacts && (
          <div className="flex flex-col gap-2.5 w-full">
            <a href="tel:+420728398282" data-testid="locked-phone"
              className="h-[50px] rounded-[14px] bg-brand text-white font-bold flex items-center justify-center gap-2">
              <Phone size={18} /> +420 728 398 282
            </a>
            <a href="mailto:petr@proyekty.cz" data-testid="locked-email"
              className="h-[50px] rounded-[14px] bg-card border border-border font-bold flex items-center justify-center gap-2">
              <Mail size={18} /> petr@proyekty.cz
            </a>
          </div>
        )}
        {v.note && <p className="text-xs text-muted-foreground mt-4">{v.note}</p>}
        <div className="mt-7 pt-4 border-t border-border/70 w-full">
          <button type="button" onClick={logout} data-testid="locked-logout"
            className="text-[13.5px] font-semibold text-muted-foreground underline">
            Odhlásit se
          </button>
        </div>
      </div>
    </div>
  );
}
